import { useState, useEffect, useRef } from 'react';
import { vars, Card } from '@etg/wings';
import { css, cx, etiColors, mediaQueries } from '@eti/styles';
import { constants } from '@eti/utils';
import { useDirection, useProperty, useSiteContext } from '@eti/providers';
import { FormSection } from 'redux-form';
import { isPrimaryFlow } from '../utils/primaryFlow';
import { getProductIllustrationId } from '../utils/icon/getProductImageryIds';
import { UPSELL_TYPES, ancillaryCardOutOfScopeProducts } from '../utils/constants';
import { formatUpsell } from '../utils/upsellParse';
import ProductDescription from './ProductDescription';
import ProductSelection from './ProductSelection';
import ReadMore from './ProductReadMore';
import Box from './ProductBox';
import Imagery from './ProductImagery';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const productWrapperStyles = upsellHighlight => css`
  background-color: ${etiColors.white};
  ${upsellHighlight && `border: 2px solid ${vars.colors.promotional.foreground}`};
  border-radius: ${vars.card.borderRadius};
  display: grid;
  grid-template-columns: 3fr 7fr;
  padding: 0;

  @media ${mediaQueries.small.only} {
    grid-template-columns: 0 1fr;
  }
`;
const childrenStyles = css`
  grid-column: 1 / 3;
  grid-row: 4;
`;
const borderStyles = isHighlighted => css`
  border-radius: ${vars.card.borderRadius};
  ${isHighlighted && `border: 2px solid ${etiColors.confirmation.focus}`};
  position: relative;
`;
const illustrationWrapperStyles = css`
  grid-column: 1;
  grid-row: 1 / 4;
  padding: 24px;

  @media ${mediaQueries.small.only} {
    display: none;
  }
`;
const illustrationStyles = (illustrationUrl, illustrationHeight) => css`
  background: url(${illustrationUrl});
  background-position: top;
  background-repeat: no-repeat;
  background-size: contain;
  height: min(100%, ${illustrationHeight.toString()}px);
  width: 100%;

  [dir='rtl'] & {
    transform: rotateY(180deg);
  }
`;
const Product = _ref => {
  let {
    additionalInformation,
    buttons,
    children,
    className,
    confirmation,
    description,
    descriptionAdditionalContent,
    hasInfants,
    hasLogo,
    icon,
    infantNoteLabel,
    isConfirmationOpen,
    isFromLabelOnly = false,
    isPersuasionOpen,
    isProductHighlighted = false,
    isSelectable = true,
    isSelected,
    notSelectedLabel,
    numberOfBounds,
    onConfirmationConfirm,
    onConfirmationDecline,
    onPersuasionConfirm,
    onPersuasionDecline,
    persuasion,
    pricePerTravelerRaw,
    priceRaw,
    productId,
    productName,
    readMore,
    selectionAdditionalBaggage,
    selectionAdditionalContent,
    shouldShowImage = true,
    shouldShowMandatoryChoiceText,
    shouldShowPrice,
    shouldShowPricePerBound,
    shouldShowPricePerTraveler,
    shouldShowToggleButtons = true,
    title,
    upSell
  } = _ref;
  const {
    isRTL
  } = useDirection();
  const {
    brand: {
      code: brand
    }
  } = useSiteContext();
  const [illustrationUrl, setIllustrationUrl] = useState('');
  const [illustrationHeight, setIllustrationHeight] = useState(10000);
  const illustrationElement = useRef(null);
  const {
    p
  } = useProperty();
  const isProductInScope = !ancillaryCardOutOfScopeProducts.find(product => product === productName);
  const isNewCardDesignEnabled = isProductInScope && p('IbeClient.AncillaryCardV2.Card.Enabled');
  const upSellObject = formatUpsell(upSell);
  const isUpsellHighlight = Boolean((upSellObject === null || upSellObject === void 0 ? void 0 : upSellObject.content) !== '' && (upSellObject === null || upSellObject === void 0 ? void 0 : upSellObject.type) === UPSELL_TYPES.PROMOTIONAL) && isPrimaryFlow();
  useEffect(() => {
    const importIllustration = async () => {
      const {
        FLIGHTNETWORK,
        GOTOGATE,
        MYTRIP
      } = constants.brands;
      const brandFolder = brand === FLIGHTNETWORK || brand === GOTOGATE || brand === MYTRIP ? brand : 'default';
      const illustrationCode = getProductIllustrationId(productName, isRTL);
      const src = await import(`@eti/assets/illustrations/${brandFolder}/${illustrationCode}.svg?url`).catch(err =>
      // eslint-disable-next-line no-console
      console.warn(`Couldn't resolve illustration path for product ${productName}. Error message: ${err}`));
      if (src) {
        setIllustrationUrl(src.default);
      }
    };
    if (isNewCardDesignEnabled) {
      importIllustration();
    }
  }, [brand, isNewCardDesignEnabled, productName, isRTL]);
  useEffect(() => {
    var _illustrationElement$;
    if ((_illustrationElement$ = illustrationElement.current) !== null && _illustrationElement$ !== void 0 && _illustrationElement$.offsetHeight) {
      var _illustrationElement$2;
      setIllustrationHeight(((_illustrationElement$2 = illustrationElement.current) === null || _illustrationElement$2 === void 0 ? void 0 : _illustrationElement$2.offsetHeight) - 1);
    }
  }, [illustrationElement]);
  return isNewCardDesignEnabled ? /*#__PURE__*/_jsx(FormSection, {
    name: productName,
    children: /*#__PURE__*/_jsxs(Card, {
      className: cx(productWrapperStyles(isUpsellHighlight), className),
      "data-abtest": "new-card-design",
      "data-offer-rate": productId,
      "data-testid": `${productName}-container`,
      children: [/*#__PURE__*/_jsx("div", {
        className: cx(illustrationWrapperStyles, 'product-image'),
        children: /*#__PURE__*/_jsx("div", {
          className: illustrationStyles(illustrationUrl, illustrationHeight),
          "data-testid": `illustration-${productName}`,
          ref: illustrationElement
        })
      }), /*#__PURE__*/_jsx(ProductDescription, {
        additionalContent: descriptionAdditionalContent,
        additionalInformation: additionalInformation,
        brand: brand,
        description: description,
        hasInfants: hasInfants,
        hasLogo: hasLogo,
        infantNoteLabel: infantNoteLabel,
        isNewCardDesignEnabled: isNewCardDesignEnabled,
        productName: productName,
        readMore: readMore,
        shouldShowImage: shouldShowImage,
        shouldShowMandatoryChoiceText: shouldShowMandatoryChoiceText,
        title: title,
        upSell: upSellObject
      }), isNewCardDesignEnabled && (readMore === null || readMore === void 0 ? void 0 : readMore.isEnabled) && /*#__PURE__*/_jsx(ReadMore, {
        content: readMore === null || readMore === void 0 ? void 0 : readMore.content,
        productName: productName
      }), /*#__PURE__*/_jsx(ProductSelection, {
        additionalContent: selectionAdditionalContent,
        buttons: buttons,
        confirmation: confirmation,
        hasInfants: hasInfants,
        infantNoteLabel: infantNoteLabel,
        isConfirmationOpen: isConfirmationOpen,
        isFromLabelOnly: isFromLabelOnly,
        isNewCardDesignEnabled: isNewCardDesignEnabled,
        isPersuasionOpen: isPersuasionOpen,
        isSelected: isSelected,
        notSelectedLabel: notSelectedLabel,
        numberOfBounds: numberOfBounds,
        onConfirmationConfirm: onConfirmationConfirm,
        onConfirmationDecline: onConfirmationDecline,
        onPersuasionConfirm: onPersuasionConfirm,
        onPersuasionDecline: onPersuasionDecline,
        persuasion: persuasion,
        priceRaw: pricePerTravelerRaw || priceRaw,
        productName: productName,
        shouldShowImage: shouldShowImage,
        shouldShowPrice: shouldShowPrice,
        shouldShowPricePerBound: shouldShowPricePerBound,
        shouldShowPricePerTraveler: shouldShowPricePerTraveler,
        title: title
      }), /*#__PURE__*/_jsx("div", {
        className: childrenStyles,
        children: children
      })]
    })
  }) : /*#__PURE__*/_jsx(FormSection, {
    name: productName,
    children: /*#__PURE__*/_jsxs("div", {
      className: cx(borderStyles(isProductHighlighted), className),
      "data-abtest": "old-card-design",
      "data-offer-rate": productId,
      "data-testid": `${productName}-container`,
      children: [/*#__PURE__*/_jsx(Box, {
        description: /*#__PURE__*/_jsx(ProductDescription, {
          additionalInformation: additionalInformation,
          description: description,
          hasInfants: hasInfants,
          hasLogo: hasLogo,
          infantNoteLabel: infantNoteLabel,
          isNewCardDesignEnabled: isNewCardDesignEnabled,
          productName: productName,
          readMore: readMore,
          shouldShowImage: shouldShowImage,
          shouldShowMandatoryChoiceText: shouldShowMandatoryChoiceText,
          title: title,
          upSell: upSellObject
        }),
        imagery: shouldShowImage && /*#__PURE__*/_jsx(Imagery, {
          iconName: icon || '',
          productName: productName
        }),
        selection: isSelectable && shouldShowToggleButtons && /*#__PURE__*/_jsx(ProductSelection, {
          additionalBaggage: selectionAdditionalBaggage,
          additionalContent: selectionAdditionalContent,
          buttons: buttons,
          confirmation: confirmation,
          hasInfants: hasInfants,
          infantNoteLabel: infantNoteLabel,
          isConfirmationOpen: isConfirmationOpen,
          isFromLabelOnly: isFromLabelOnly,
          isNewCardDesignEnabled: isNewCardDesignEnabled,
          isPersuasionOpen: isPersuasionOpen,
          isSelected: isSelected,
          notSelectedLabel: notSelectedLabel,
          numberOfBounds: numberOfBounds,
          onConfirmationConfirm: onConfirmationConfirm,
          onConfirmationDecline: onConfirmationDecline,
          onPersuasionConfirm: onPersuasionConfirm,
          onPersuasionDecline: onPersuasionDecline,
          persuasion: persuasion,
          priceRaw: pricePerTravelerRaw || priceRaw,
          productName: productName,
          shouldShowImage: shouldShowImage,
          shouldShowPrice: shouldShowPrice,
          shouldShowPricePerBound: shouldShowPricePerBound,
          shouldShowPricePerTraveler: shouldShowPricePerTraveler,
          title: title
        })
      }), children]
    })
  });
};
export default Product;
//# sourceMappingURL=Product.js.map